import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon, MapPin, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import BusinessCard from "../components/BusinessCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Search() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/commerces/search`, { params: query ? { q: query } : {} });
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get("q") || "";
    setQ(initial);
    load(initial);
  }, [searchParams]);

  const onSearch = (e) => {
    e.preventDefault();
    setSearchParams(q ? { q } : {});
    load(q);
  };

  const useGeo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}
    );
  };

  const distance = (c) => {
    if (!userLoc || !c.lat || !c.lng) return null;
    const R = 6371;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(c.lat - userLoc.lat);
    const dLng = toRad(c.lng - userLoc.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(userLoc.lat)) * Math.cos(toRad(c.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const sorted = userLoc
    ? [...results].sort((a, b) => (distance(a) ?? 9999) - (distance(b) ?? 9999))
    : results;

  return (
    <div>
      {/* HEADER */}
      <section className="relative border-b border-border">
        <div className="container relative mx-auto px-4 md:px-8 pt-10 pb-10 md:pt-14 md:pb-14">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-subtle hover:text-ink mb-6" data-testid="back-home-link">
            <ArrowLeft size={14} /> Accueil
          </Link>
          <p className="overline mb-3">Rechercher</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-ink tracking-tighter leading-tight">
            Trouvez un établissement<br />
            <span className="text-brand">et prenez votre ticket à distance.</span>
          </h1>

          <form onSubmit={onSearch} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-3xl" data-testid="search-form">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-subtle" size={18} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Coiffeur, mairie, garage, clinique, restaurant..."
                className="h-14 pl-12 pr-4 text-base border-2 border-ink focus-visible:ring-0 focus-visible:border-brand rounded-md"
                data-testid="search-input"
              />
            </div>
            <Button type="button" variant="outline" onClick={useGeo} className="h-14 border-2 border-ink rounded-md font-medium" data-testid="geo-btn">
              <MapPin size={18} className="mr-2" /> {userLoc ? "Localisé" : "Près de moi"}
            </Button>
            <Button type="submit" className="h-14 bg-brand hover:bg-brand-hover rounded-md text-white font-medium px-8" data-testid="search-btn">
              Rechercher
            </Button>
          </form>
        </div>
      </section>

      {/* RESULTS */}
      <section className="container mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline mb-2">Établissements disponibles</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-ink tracking-tight">
              {loading ? "Recherche…" : `${sorted.length} résultat${sorted.length !== 1 ? "s" : ""}`}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-80 bg-surface animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <p className="text-ink font-medium">Aucun établissement disponible pour le moment.</p>
            <p className="text-subtle text-sm mt-2">Revenez plus tard ou affinez votre recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((c) => (
              <BusinessCard key={c.id} c={c} distanceKm={distance(c)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
